import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import type { FeatureCollection, Position } from "geojson";
import { Map as MapIcon } from "lucide-react";
import { useMemo } from "react";

import { api } from "../api/client";

/** Silhouette du camp ou du terrain libre d'un dossier (couche de limite, en gris ardoise : jeton `ink-soft`) :
 *  chaque dossier se reconnaît à son terrain (accueil), et le lieu choisi se reconnaît à côté de sa liste (Données
 *  initiales). Sans lieu choisi, ou sans limite connue, une icône de carte. Composant partagé unique (R0). */

function polygons(collection: FeatureCollection): Position[][][] {
  const out: Position[][][] = [];
  for (const feature of collection.features) {
    const g = feature.geometry;
    if (g?.type === "Polygon") out.push(g.coordinates);
    else if (g?.type === "MultiPolygon") out.push(...g.coordinates);
  }
  return out;
}

/** Aire (degrés², à l'échelle près) de l'anneau extérieur. */
function area(ring: Position[]): number {
  let sum = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    sum += (ring[j]![0]! + ring[i]![0]!) * (ring[j]![1]! - ring[i]![1]!);
  }
  return Math.abs(sum / 2);
}

/** Tracé SVG ajusté au carré `size` (marge `pad`), en équirectangulaire corrigé de la latitude. Un camp fait
 *  parfois de parcelles éloignées (11 à Mourmelon, sur 50 km) : seules celles d'au moins 8 % de la plus
 *  grande sont gardées, pour que la silhouette montre le camp et non des confettis. */
export function outlinePath(collection: FeatureCollection, size: number, pad = 4): string | null {
  const parts = polygons(collection).filter((polygon) => polygon[0] && polygon[0].length >= 3);
  const largest = Math.max(0, ...parts.map((polygon) => area(polygon[0]!)));
  const all = parts.filter((polygon) => area(polygon[0]!) >= 0.08 * largest).flat();
  const points = all.flat();
  if (points.length < 3) return null;
  const lat0 = (points.reduce((sum, p) => sum + p[1]!, 0) / points.length) * (Math.PI / 180);
  const project = (p: Position): [number, number] => [p[0]! * Math.cos(lat0), -p[1]!];
  const projected = points.map(project);
  const xs = projected.map((p) => p[0]);
  const ys = projected.map((p) => p[1]);
  const [x0, x1, y0, y1] = [Math.min(...xs), Math.max(...xs), Math.min(...ys), Math.max(...ys)];
  const scale = (size - 2 * pad) / Math.max(x1 - x0, y1 - y0, 1e-9);
  const dx = (size - (x1 - x0) * scale) / 2;
  const dy = (size - (y1 - y0) * scale) / 2;
  return all
    .map((ring) => {
      const coords = ring.map(project).map(([x, y]) => `${(dx + (x - x0) * scale).toFixed(1)} ${(dy + (y - y0) * scale).toFixed(1)}`);
      return `M${coords.join("L")}Z`;
    })
    .join("");
}

export function CampOutline({ slug, size = 44, className }: { slug: string | null | undefined; size?: number; className?: string }) {
  const { data } = useQuery({
    queryKey: ["camp-outline", slug],
    queryFn: () => api.layer(slug!, "boundary"),
    enabled: Boolean(slug),
    staleTime: Infinity,
    gcTime: Infinity,
  });
  const path = useMemo(() => (data ? outlinePath(data, size) : null), [data, size]);
  return (
    <span
      className={clsx("inline-flex shrink-0 items-center justify-center rounded-lg bg-canvas", className)}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {path ? (
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <path d={path} fill="var(--color-ink-soft)" fillOpacity={0.08} stroke="var(--color-ink-soft)" strokeOpacity={0.75} strokeWidth={1.25} strokeLinejoin="round" fillRule="evenodd" />
        </svg>
      ) : (
        <MapIcon size={Math.round(size * 0.42)} className="text-ink-muted" />
      )}
    </span>
  );
}
